/**
 * ui-scope.ts — zúžení UI podle dedicated URL (CodAble hub).
 *
 * Publikovaný bod `codable-<hash>.…` není druhý dashboard — je to tentýž
 * dashboard zúžený na Codable a související moduly. Alias je routing;
 * prezentaci musí držet UI samo. Claude to zkusil jen přes
 * `X-Acm-Ui-Scope` v NPM advanced_config — `proxy_set_header` v server
 * bloku NPM zahodí, jakmile location / nastaví vlastní hlavičky, takže
 * na živé URL zůstalo celé ACM.
 *
 * Zdroj pravdy je hostname dedicated URL (to je ta plocha). Hlavička
 * z nginx je bonus, ne podmínka. Klient si tím může nejvýš ubrat.
 */

export const UI_SCOPE_PRODUCTS: Record<string, {
  home: string;
  phoneHome: string;
  modules: readonly string[];
}> = {
  codable: {
    home: '/modules/codable',
    phoneHome: '/phone/codable',
    modules: ['codable', 'llm-profiles', 'chat'],
  },
  mail: {
    home: '/mail',
    phoneHome: '/mail',
    modules: ['mail'],
  },
};

const HOST_PRODUCT: Array<{ re: RegExp; scope: string }> = [
  { re: /^codable-[a-z0-9]+$/, scope: 'codable' },
  { re: /^webmail(-[a-z0-9]+)?$/, scope: 'mail' },
];

/** Scope z hostname dedicated URL. */
export function uiScopeFromHost(hostname: string): string | null {
  const host = hostname.split(':')[0]?.trim().toLowerCase() ?? '';
  const first = host.split('.')[0] ?? '';
  for (const { re, scope } of HOST_PRODUCT) {
    if (re.test(first)) return scope;
  }
  return null;
}

function injectedScope(): string | null {
  const raw = (globalThis as { __ACM_UI_SCOPE?: unknown }).__ACM_UI_SCOPE;
  return typeof raw === 'string' && raw in UI_SCOPE_PRODUCTS ? raw : null;
}

/** Produkt, na který je tato URL zúžená, nebo null pro plné ACM. */
export function uiScope(): string | null {
  const injected = injectedScope();
  if (injected) return injected;
  if (typeof window !== 'undefined') return uiScopeFromHost(window.location.hostname);
  return null;
}

export function uiScopeModules(scope: string): readonly string[] {
  return UI_SCOPE_PRODUCTS[scope]?.modules ?? [];
}

export function uiScopeAllowsModule(scope: string, moduleName: string): boolean {
  return uiScopeModules(scope).includes(moduleName);
}

/** Kam patří uživatel na zúžené URL. */
export function uiScopeHome(scope: string, phone = false): string {
  const product = UI_SCOPE_PRODUCTS[scope];
  if (!product) return '/';
  return phone ? product.phoneHome : product.home;
}

function pathIsModule(pathname: string, moduleName: string): boolean {
  for (const prefix of [`/modules/${moduleName}`, `/core/${moduleName}`]) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return true;
  }
  if (moduleName === 'mail' && (pathname === '/mail' || pathname.startsWith('/mail/'))) {
    return true;
  }
  return false;
}

/**
 * Cesty povolené na zúžené URL: vlastní + související moduly, telefonní
 * Codable, start (dlaždice se filtrují), profil kvůli odhlášení.
 */
export function uiScopeAllowsPath(scope: string, pathname: string): boolean {
  const product = UI_SCOPE_PRODUCTS[scope];
  if (!product) return true;
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return true;
  if (pathname === '/core/wiki' || pathname.startsWith('/core/wiki/')) return true;
  if (pathname === '/start' || pathname === '/phone' || pathname === '/phone/') return true;
  if (scope === 'codable' && (pathname === '/phone/codable' || pathname.startsWith('/phone/codable/'))) {
    return true;
  }
  return product.modules.some((mod) => pathIsModule(pathname, mod));
}

/** Dlaždice Start jen pro povolené cesty zúžené plochy. */
export function uiScopeAllowsStartPath(pathname: string): boolean {
  const scope = uiScope();
  if (!scope) return true;
  return uiScopeAllowsPath(scope, pathname);
}
